import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom';
  /**
   * Render the bubble in a portal (position: fixed) so it escapes
   * `overflow:hidden/auto` ancestors — e.g. a scrollable dropdown options panel.
   * Default false keeps the lightweight absolute-positioned bubble.
   */
  portal?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, className, position = 'top', portal = false }) => {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState<'center' | 'left' | 'right'>('center');
  const [placement, setPlacement] = useState(position);
  const [fixed, setFixed] = useState<{ top: number; left: number } | null>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const wrapRect = wrapRef.current.getBoundingClientRect();

    if (portal) {
      // Position: fixed, centered over the trigger; flip below if no room above.
      const place = position === 'top' && wrapRect.top < 48 ? 'bottom' : position;
      setPlacement(place);
      setFixed({
        left: wrapRect.left + wrapRect.width / 2,
        top: place === 'top' ? wrapRect.top - 8 : wrapRect.bottom + 8,
      });
      return;
    }

    // Non-portal: absolute bubble, choose horizontal alignment + vertical flip.
    const viewW = window.innerWidth;
    if (wrapRect.right > viewW - 100) setAlign('right');
    else if (wrapRect.left < 100) setAlign('left');
    else setAlign('center');

    if (position === 'top' && wrapRect.top < 40) setPlacement('bottom');
    else setPlacement(position);
  }, [open, position, portal]);

  // whitespace-pre-line preserves `\n` in the source text so multi-line
  // tooltips (e.g. EntrySourceBadge's primary + chain-history lines) render
  // on separate lines. Plain single-line tooltips are unaffected.
  const bubbleClass = 'px-2.5 py-1.5 rounded-md text-xs font-medium pointer-events-none max-w-xs break-words whitespace-pre-line bg-gray-900 dark:bg-gray-700 text-white shadow-lg';

  return (
    <span
      ref={wrapRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {open && text && portal && fixed && createPortal(
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            left: fixed.left,
            top: fixed.top,
            transform: placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
            zIndex: 9999,
          }}
          className={bubbleClass}
        >
          {text}
        </span>,
        document.body,
      )}
      {open && text && !portal && (
        <span
          ref={tipRef}
          role="tooltip"
          className={cn(
            'absolute z-50',
            bubbleClass,
            placement === 'top' && 'bottom-full mb-2',
            placement === 'bottom' && 'top-full mt-2',
            align === 'center' && 'left-1/2 -translate-x-1/2',
            align === 'right' && 'right-0',
            align === 'left' && 'left-0',
          )}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default Tooltip;
